<script setup>
import { ElCarousel, ElCarouselItem } from "element-plus";
import { ref, useTemplateRef, watch, defineExpose } from "vue";
import { defineProps } from "vue";
import { computed } from "vue";
import AppLinkKintz from "./AppLinkKintz.vue";
import LoadingSpinner from "@/components/utilities/LoadingSpinner.vue";

const props = defineProps({
  autoplay: {
    type: Boolean,
    default: true,
  },
  items: {
    type: Array,
    default: () => [],
  },
  activeIndex: {
    type: Number,
    default: 0,
  },
  type: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  showTimelineStatus: {
    type: Boolean,
    default: false,
  },
  showTimeline: {
    type: Boolean,
    default: true,
  }
});

const displayIndex = ref(0);
const refreshKey = ref(0);
const carousel = useTemplateRef("carousel");

const autoplay = computed(() => props.autoplay);
const items = computed(() => props.items);
const activeIndex = computed(() => props.activeIndex);
const type = computed(() => props.type);
const loading = computed(() => props.loading);
const showTimelineStatus = computed(() => props.showTimelineStatus);

const getBannerUrl = (item) => {
  if (!item || !item.banner_url) {
    return null;
  }

  const rawUrl = String(item.banner_url).trim();

  if (!rawUrl) {
    return null;
  }

  if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) {
    return rawUrl;
  }

  return `https://${rawUrl}`;
};

const handleCarouselChange = (currentIndex) => {
  setTimeout(() => {
    displayIndex.value = currentIndex;
  }, 1000);
};

const handleClickCard = (index) => {
  carousel.value.setActiveItem(index);
};

const updateRefreshKey = () => {
  refreshKey.value++
}

watch(activeIndex, (value) => {
  if (typeof value === "number" && items.value.length) {
    displayIndex.value = value;
    carousel.value.setActiveItem(value);
  }
  refreshKey.value++;
});

defineExpose({
  updateRefreshKey,
})
</script>
<template>
  <div
    class="w-full flex flex-col relative aspect-video md:h-[500px] lg:h-[600px] xl:h-[700px]"
    :key="refreshKey"
    :class="{
      'flex justify-center items-center': loading,
    }"
  >
    <ElCarousel
      indicator-position="outside"
      class="h-full"
      :initial-index="displayIndex"
      height="100%"
      :autoplay="autoplay"
      :motion-blur="true"
      ref="carousel"
      :type="type"
      v-if="!loading"
      @change="handleCarouselChange"
    >
      <ElCarouselItem v-for="(item, index) in items" :key="index">
        <div class="w-full h-full relative bg-kintz-primary-1">
          <img
            v-if="item.image"
            v-lazy="{
              src: item.image,
              error:
                'https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg',
              loading:
                'https://via.placeholder.com/800x450?text=Thumbnail%20Loading...',
            }"
            class="w-full h-full sm:object-cover sm:object-center"
            alt="Banner Image"
          />
          <div
            v-if="item.is_draft"
            class="absolute top-0 right-0 bg-kintz-primary-4 px-4 py-3 text-sm font-semibold uppercase rounded-bl-lg shadow-lg shadow-kintz-primary-3"
          >
            Draft
          </div>
          <div
            class="absolute top-0 left-0 bg-kintz-primary-4 px-4 py-3 text-sm font-semibold uppercase rounded-br-lg shadow-lg shadow-kintz-primary-3"
            :class="{
              'text-kintz-secondary-3': item.timeline_status === 'ongoing',
              'text-kintz-secondary-1': item.timeline_status === 'overdue',
              'text-kintz-primary-2': item.timeline_status === 'pending',
            }"
            v-if="showTimelineStatus && item.timeline_status"
          >
            {{ item.timeline_status.replace("_", " ") }}
          </div>
        </div>
        <component
          :is="item.is_clickable && getBannerUrl(item) ? 'a' : 'div'"
          class="w-full flex absolute h-full flex-col top-0 left-0"
          :class="{
            'cursor-pointer': item.is_clickable && getBannerUrl(item),
          }"
          :href="item.is_clickable ? getBannerUrl(item) : undefined"
          :target="item.is_clickable && getBannerUrl(item) ? '_blank' : undefined"
          :rel="item.is_clickable && getBannerUrl(item) ? 'noopener noreferrer' : undefined"
        >
          <div
            class="relative bg-no-repeat bg-center bg-fixed w-full h-full flex justify-center items-center carousel-bg-image"
            :style="{
              backgroundImage: item.image ? `url(${item.image})` : 'none',
            }"
          >
            <div
              class="w-full absolute bottom-40 right-0 lg:px-10 py-3 px-5 flex flex-col gap-2 lg:gap-3"
            >
              <div class="w-full flex justify-start items-center">
                <h1
                  :style="{
                    color: item.title_text_color || item.title_color || '#fff',
                  }"
                  class="text-2xl xl:text-4xl font-semibold"
                >
                  {{ item.title }}
                </h1>
              </div>
              <div class="flex justify-start items-center">
                <p
                  class="font-normal text-lg lg:text-xl"
                  :style="{
                    color: item.description_text_color || item.description_color || '#fff',
                  }"
                >
                  {{ item.description }}
                </p>
              </div>
              <div class="w-full flex flex-row gap-3 justify-start items-center">
                <AppLinkKintz
                  v-for="(link, index) in item.links"
                  :key="index"
                  :background-color="link.bg_color"
                  :text-color="link.text_color"
                  :label="link.label"
                  :url="link.url"
                  :open-in-new-tab="
                    link.open_in_new_tab === 'true' ||
                    link.open_in_new_tab === true
                  "
                  :rounded="link.rounded === 'true' || link.rounded === true"
                  :size="link.size"
                />
              </div>
            </div>
          </div>
        </component>
      </ElCarouselItem>
    </ElCarousel>
    <div
      class="absolute bottom-2 w-full right-0 h-36 px-10 py-3 mx-auto flex"
      v-if="!loading && showTimeline"
    >
      <div
        class="flex-1 bg-kintz-primary-3 rounded-lg shadow-inner shadow-kintz-primary-3 backdrop-blur-sm bg-opacity-30 flex flex-row gap-2 justify-center items-center lg:gap-3 p-2 lg:p-4 overflow-x-auto"
      >
        <div
          v-for="(item, index) in items"
          :key="index"
          class="w-40 h-full flex flex-col rounded-lg border cursor-pointer transition-all duration-700 ease-in-out hover:scale-110 overflow-hidden"
          @click="handleClickCard(index)"
          :class="{
            'border-kintz-primary-4 hover:border-kintz-primary-2':
              displayIndex !== index,
            'border-kintz-primary-2 shadow-inner shadow-kintz-primary-2':
              displayIndex === index,
          }"
        >
          <img
            v-if="item.image"
            v-lazy="{
              src: item.image,
              error:
                'https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg',
              loading:
                'https://via.placeholder.com/800x450?text=Thumbnail%20Loading...',
            }"
            class="w-full h-full object-cover object-center"
          />
        </div>
      </div>
    </div>

    <LoadingSpinner v-if="loading" />
  </div>
</template>

<style scoped>
.el-carousel__item h3 {
  display: flex;
  color: #475669;
  opacity: 0.75;
  line-height: 300px;
  margin: 0;
}

.el-carousel__item:nth-child(2n) {
  background-color: #99a9bf;
}

.el-carousel__item:nth-child(2n + 1) {
  background-color: #d3dce6;
}

.carousel-bg-image {
  background-size: cover;
  background-position: center;
}

@media (min-width: 1024px) {
  .carousel-bg-image {
    background-size: 100% 100%;
  }
}
</style>
